import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Link, 
  Button, 
  TextField, 
  Stack, 
  FormControlLabel, 
  Checkbox 
} from '@mui/material';
import { ContentCopy, Add, DeleteOutline, Star, CallMade } from '@mui/icons-material';
import { PseudoLink2, SimpleDialog, useCopyText } from '../../primitives';
import { SmallAddButton } from './Primitives';

export interface DescribedAddress {
  value: string;
  description?: string;
  is_main?: boolean;
}

interface Props {
  items?: DescribedAddress[];
  type: 'email' | 'phone';
  onChange?: (items: DescribedAddress[]) => void;
  showMainOnly?: boolean;
}

export const DescribedAddressList: React.FC<Props> = ({ items = [], type, onChange, showMainOnly }) => {
  const copyText = useCopyText(); 

  // null = closed, -1 = adding new, >= 0 = editing existing
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<DescribedAddress>({ value: '', description: '', is_main: false });

  const linkPrefix = type === 'email' ? 'mailto:' : 'tel:';

  const handleCopy = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    copyText(text);
  };

  const handleEditOpen = (index: number) => {
    setEditingIndex(index);
    setDraft(items[index]);
  };

  const handleAddOpen = () => {
    setEditingIndex(-1);
    setDraft({ value: '', description: '', is_main: items.length === 0 }); // Default to main if it's the first one
  };

  const handleClose = () => {
    setEditingIndex(null);
  };

  const handleSave = () => {
    if (!draft.value.trim() || !onChange) return;
    
    // Copy the items array so we can mutate it
    let newItems = [...items];
    
    // If the saved item is marked as main, unset is_main on all other items
    if (draft.is_main) {
      newItems = newItems.map(item => ({ ...item, is_main: false }));
    }

    if (editingIndex === -1) {
      newItems.push(draft);
    } else if (editingIndex !== null) {
      newItems[editingIndex] = draft;
    }
    
    onChange(newItems);
    handleClose();
  };

  const handleDelete = () => {
    if (editingIndex !== null && editingIndex !== -1 && onChange) {
      const newItems = items.filter((_, i) => i !== editingIndex);
      // Optional logic: if we delete the main item, and there are others left, make the first remaining one main?
      if (draft.is_main && newItems.length > 0) {
        newItems[0].is_main = true;
      }
      onChange(newItems);
    }
    handleClose();
  };

  // Logic for showMainOnly prop
  const displayItems = React.useMemo(() => {
    if (!showMainOnly) return items;
    if (items.length === 0) return [];
    
    const mainItem = items.find(i => i.is_main);
    return mainItem ? [mainItem] : [items[0]]; // fallback to first item if none is main
  }, [items, showMainOnly]);

  return (
    <Box>
      <Stack spacing={1} rowGap={0} direction={"row"} flexWrap={"wrap"} alignItems="center" useFlexGap>
        {displayItems.map((item, idx) => (
          <Box 
            key={`${item.value}_${idx}`} 
            sx={{ 
              // Actions are hidden by default, visible on parent hover
              position: "relative",
              '& .address-actions': { opacity: 0, transition: 'opacity 0.2s', position: "absolute", right: 0, paddingLeft: 2, background: "linear-gradient(to left, rgba(255,255,255,0.85) 75%, rgba(255,255,255,0))" },
              '&:hover .address-actions': { opacity: 1 } 
            }}
          >
            <Stack direction="row" alignItems="center">
              {/* Star icon for the main item (only if we aren't in showMainOnly mode) */}
              {!showMainOnly && item.is_main && (
                <Star sx={{ fontSize: 14, color: 'primary.main', mr: 0.5 }} />
              )}
              
              <Link 
                href={`${linkPrefix}${item.value}`}
                onClick={onChange
                  ? e => {
                    e.preventDefault();
                    handleEditOpen(items.findIndex(i => i === item));
                  }
                  : undefined
                }
                sx={{ 
                  wordBreak: 'break-all', 
                  color: 'primary.main',
                  textDecoration: 'none',
                  // Thinner dotted underline on hover
                  '&:hover': {
                    textDecoration: 'underline dotted',
                    textDecorationThickness: '1px',
                    textUnderlineOffset: '2px'
                  }
                }}
              >
                {item.value}
              </Link>
              
              <Stack direction="row" className="address-actions" sx={{ ml: 1 }}>
                <IconButton 
                  onClick={(e) => handleCopy(e, item.value)}
                  sx={{ width: 22, height: 22 }}
                >
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
                {onChange && (
                  <IconButton 
                    href={`${linkPrefix}${item.value}`}
                    sx={{ width: 22, height: 22 }}
                  >
                    <CallMade sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
              </Stack>
            </Stack>

            {item.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.25 }}>
                {item.description}
              </Typography>
            )}
          </Box>
        ))}

        {!showMainOnly && onChange && (
          <Box>
            <SmallAddButton iconOnly={!!displayItems.length} icon={<Add />} onClick={handleAddOpen}>
              добавить {type === 'email' ? 'email' : 'телефон'}
            </SmallAddButton>
          </Box>
        )}
      </Stack>

      {/* ADD / EDIT DIALOG */}
      <SimpleDialog 
        isOpen={editingIndex !== null} 
        close={handleClose} 
        dialogTitle={editingIndex === -1 ? "Добавить" : "Редактировать"}
        save={handleSave}
        noFullscreen
        maxWidth="xs"
        saveLabel="ok"
        submitOnModEnter
      >
        <Stack spacing={2} mt={1}>
          <TextField
            placeholder={type === 'email' ? 'Email' : 'Телефон'}
            value={draft.value}
            onChange={(e) => setDraft({ ...draft, value: e.target.value })}
            fullWidth
            autoFocus
            size="small"
          />
          <TextField
            placeholder="Описание"
            value={draft.description || ''}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            fullWidth
            size="small"
            multiline
          />
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={draft.is_main || false}
                onChange={(e) => setDraft({ ...draft, is_main: e.target.checked })}
                size="small"
              />
            }
            label={<Typography variant="body2">Основной</Typography>}
          />

          {/* Delete button shown only when editing an existing record */}
          {editingIndex !== -1 && (
            <Box mt={1}>
              <Button 
                color="error" 
                size="small" 
                startIcon={<DeleteOutline />} 
                onClick={handleDelete}
              >
                Удалить адрес
              </Button>
            </Box>
          )}
        </Stack>
      </SimpleDialog>
    </Box>
  );
};


interface DescribedAddressLinksProps {
  items?: DescribedAddress[];
  noSeparators?: boolean;
  showMainOnly?: boolean;
}

export const DescribedAddressLinks = (props: DescribedAddressLinksProps) => {
  const copyText = useCopyText();

  let items = props.items || [];
  if(props.showMainOnly) {
    items = items.filter(x => x.is_main);
    if(!items.length) {
      items = props.items || [];
    }
    items.slice(0,1);
  }
  
  return (
    <>
      {items.map((e, idx) => (
        <React.Fragment key={`${e.value}_${idx}`}>
          <PseudoLink2 onClick={ex => { ex.stopPropagation(); copyText(e.value); }}>{e.value}</PseudoLink2>
          {!!items?.length && (idx < items.length - 1) && !props.noSeparators && ", "}
        </React.Fragment>
        ))}
    </>
  )
}